'use client'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'

type Prediction = {
  placeId: string
  description: string
}

type PlaceDetails = {
  address: string
  city: string
  state: string
  zip: string
  lat: number | null
  lng: number | null
}

type AddressAutocompleteInputProps = {
  value: string
  onValueChange: (value: string) => void
  onPlaceSelected: (place: PlaceDetails) => void
  id?: string
  placeholder?: string
  className?: string
  required?: boolean
  disabled?: boolean
}

export function AddressAutocompleteInput({
  value,
  onValueChange,
  onPlaceSelected,
  id,
  placeholder = 'Address',
  className,
  required,
  disabled,
}: AddressAutocompleteInputProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null)
  const hasUserEditedRef = useRef(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [])

  useEffect(() => {
    const query = value.trim()
    if (disabled || !query || query.length < 3) {
      setPredictions([])
      setError(null)
      setIsLoading(false)
      return
    }

    if (!hasUserEditedRef.current || selectedPlaceId) return

    const controller = new AbortController()
    const timeout = window.setTimeout(async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await fetch(`/api/google-places?input=${encodeURIComponent(query)}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to load address suggestions.')
        }

        const payload = (await response.json()) as {
          predictions?: Prediction[]
        }
        const nextPredictions = Array.isArray(payload.predictions) ? payload.predictions : []
        setPredictions(nextPredictions)
        setIsOpen(true)
      } catch (err) {
        if (controller.signal.aborted) return
        setPredictions([])
        setError(err instanceof Error ? err.message : 'Failed to load address suggestions.')
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }, 250)

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [disabled, selectedPlaceId, value])

  const handleChange = (nextValue: string) => {
    hasUserEditedRef.current = true
    onValueChange(nextValue)
    setSelectedPlaceId(null)
    setError(null)
    if (nextValue.trim().length < 3) {
      setPredictions([])
      setIsOpen(false)
    }
  }

  const handleSelectPrediction = async (prediction: Prediction) => {
    setSelectedPlaceId(prediction.placeId)
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/google-places?placeId=${encodeURIComponent(prediction.placeId)}`)
      if (!response.ok) {
        throw new Error('Failed to load selected address details.')
      }

      const payload = (await response.json()) as PlaceDetails
      const nextAddress = payload.address || prediction.description
      onValueChange(nextAddress)
      onPlaceSelected({
        address: nextAddress,
        city: payload.city || '',
        state: payload.state || '',
        zip: payload.zip || '',
        lat: typeof payload.lat === 'number' ? payload.lat : null,
        lng: typeof payload.lng === 'number' ? payload.lng : null,
      })
      setPredictions([])
      setIsOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load selected address details.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('relative', className)} ref={wrapperRef}>
      <Input
        id={id}
        required={required}
        disabled={disabled}
        autoComplete="street-address"
        placeholder={placeholder}
        value={value}
        onFocus={() => {
          if (predictions.length > 0) {
            setIsOpen(true)
          }
        }}
        onChange={(event) => handleChange(event.target.value)}
      />

      {(isOpen && predictions.length > 0) || isLoading ? (
        <div className="theme-card absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-md border shadow-lg">
          {isLoading ? (
            <div className="p-3 text-sm">Searching addresses...</div>
          ) : (
            predictions.map((prediction) => (
              <button
                key={prediction.placeId}
                type="button"
                className="block w-full border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-black/5"
                onClick={() => handleSelectPrediction(prediction)}
              >
                {prediction.description}
              </button>
            ))
          )}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  )
}
